import React from 'react';
import { Box } from 'src/design/atoms/Box';
import { Loader } from 'src/design/atoms/Loader';
import { Text } from 'src/design/atoms/Text';
import { Tabs } from 'src/design/molecules/Tabs';
import { useNavigate, useParams } from 'react-router-dom';
import { SubmittalDto } from 'src/api/FiretrolApiClient';
import { useSubmittal } from 'src/api/queries/submittals/useSubmittalQueries';
import { useJobs } from 'src/hooks/useJobs';
import {
  SubmittalForm,
  SubmittalFormSection,
} from 'src/routes/Jobs/Submittals/components/SubmittalForm';
import {
  HistoryList,
  SubmittalHistoryDto,
} from 'src/app/workflows/HistoryList';
import { NotificationsTab } from 'src/app/workflows/NotificationsTab';
import { Header } from 'src/design/organisms/Header';
import { jobBreadcrumbs } from 'src/app/workflows/jobBreadcrumbs';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';

export const ViewSubmittal: React.FC = () => {
  const params = useParams<{ jobId: string; submittalId: string }>();
  const navigate = useNavigate();
  const { selectedJob, users } = useJobs();
  const [tab, setTab] = React.useState<SubmittalFormSection>('details');
  const { data: submittal, isLoading: loading } = useSubmittal(
    selectedJob?.id,
    params.submittalId,
  );
  const submittalHistory = submittal?.history ?? [];

  if (loading) return <Loader />;
  if (!submittal || !selectedJob) return <Text>Unable to find submittal</Text>;

  const jobUsers = users ?? [];

  return (
    <>
      <Header
        breadcrumbProps={{
          items: jobBreadcrumbs(
            selectedJob,
            { text: 'Submittals', href: `/jobs/${selectedJob.id}/submittals` },
            submittal.submittalNumber || 'No Submittal Number',
          ),
        }}
        title={submittal.submittalNumber || 'View Submittal'}
        helpText="View and update this submittal's details, attachments, and notifications."
        actions={
          <SecondaryButton
            onClick={() => navigate(`/jobs/${selectedJob.id}/submittals`)}
          >
            Cancel
          </SecondaryButton>
        }
      />

      <Tabs
        value={tab}
        onChange={(value) =>
          setTab((value as SubmittalFormSection) || 'details')
        }
        mt="md"
      >
        <Tabs.List>
          <Tabs.Tab value="details">Details</Tabs.Tab>
          <Tabs.Tab value="attachments">Attachments</Tabs.Tab>
          <Tabs.Tab value="notifications">Notifications</Tabs.Tab>
          <Tabs.Tab value="history">History</Tabs.Tab>
        </Tabs.List>
      </Tabs>

      <Box mt="md">
        <SubmittalForm
          initialValues={submittal}
          selectedJob={selectedJob}
          activeSection={tab}
          jobUsers={jobUsers}
          onCreated={(s) =>
            navigate(`/jobs/${selectedJob.id}/submittals/${s.id}`)
          }
        >
          {tab === 'notifications' && (
            <NotificationsTab
              resourceType="submittal"
              entityNoun="Submittal"
              attachPdfLabel="Attach Submittal PDF"
              jobId={selectedJob.id}
              entityId={submittal.id}
              jobUsers={jobUsers}
              customerNumber={selectedJob.customerNumber?.trim() ?? ''}
            />
          )}

          {tab === 'history' && <HistoryList history={submittalHistory} />}
        </SubmittalForm>
      </Box>
    </>
  );
};
